import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ENROLLMENT_STATUSES, ENROLLMENT_STATUS_LABEL } from '@doit/shared';
import { ArrowRightLeft, Plus, Search, UserMinus } from 'lucide-react';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { Button, Card, EmptyState, Spinner } from '../../components/ui';
import { Select } from '../../components/form';
import { Badge } from '../../components/badges';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../api/client';
import {
  useEnrollStudents,
  useGroupEnrollments,
  useGroups,
  useRemoveEnrollment,
  useStudents,
  useTransferEnrollment,
  useUpdateEnrollment,
} from '../../api/queries';
import type { GroupEnrollment } from '../../api/types';
import { useDebounced } from '../../lib/useDebounced';
import { formatPercent } from '../../lib/format';
import { CertBadge, DoneCell } from '../../components/certificate';

export function GroupStudents({ groupId, canEdit }: { groupId: string; canEdit: boolean }) {
  const toast = useToast();
  const { data: enrollments, isLoading } = useGroupEnrollments(groupId);
  const removeEnrollment = useRemoveEnrollment();
  const updateEnrollment = useUpdateEnrollment();

  const [addOpen, setAddOpen] = useState(false);
  const [toRemove, setToRemove] = useState<GroupEnrollment | null>(null);
  const [toTransfer, setToTransfer] = useState<GroupEnrollment | null>(null);

  const active = (enrollments ?? []).filter((e) => e.status === 'ACTIVE');

  async function handleRemove() {
    if (!toRemove) return;
    try {
      await removeEnrollment.mutateAsync(toRemove.id);
      toast.success('სტუდენტი ამოღებულია ჯგუფიდან');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ამოღება ვერ მოხერხდა');
    } finally {
      setToRemove(null);
    }
  }

  async function changeStatus(id: string, status: string) {
    try {
      await updateEnrollment.mutateAsync({ id, data: { status } });
      toast.success('სტატუსი შეიცვალა');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ვერ შეიცვალა');
    }
  }

  return (
    <>
      <Card
        title={`სტუდენტები (${active.length})`}
        action={
          canEdit ? (
            <Button
              size="sm"
              variant="secondary"
              icon={<Plus className="size-4" />}
              onClick={() => setAddOpen(true)}
            >
              ჩარიცხვა
            </Button>
          ) : undefined
        }
      >
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (enrollments ?? []).length === 0 ? (
          <EmptyState
            title="ჯგუფში სტუდენტები არ არიან"
            description="დაამატე სტუდენტები, რომ ლექტორმა დასწრება აღრიცხოს."
          />
        ) : (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="border-y border-line bg-canvas/70 text-left text-[11px] uppercase tracking-wide text-ink-mute">
                <tr>
                  <th className="px-5 py-2.5 font-semibold">სტუდენტი</th>
                  {canEdit && <th className="px-4 py-2.5 font-semibold">ტელეფონი</th>}
                  {/* წარსული ეტაპები მენეჯერის ხედია — ლექტორი მიმდინარეზე მუშაობს */}
                  {canEdit && (
                    <th className="px-4 py-2.5 text-right font-semibold">წინა ეტაპი</th>
                  )}
                  <th className="px-4 py-2.5 font-semibold">შესრულებული</th>
                  <th className="px-4 py-2.5 font-semibold">სერტიფიკატი</th>
                  <th className="px-4 py-2.5 font-semibold">სტატუსი</th>
                  <th className="px-5 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {(enrollments ?? []).map((e) => (
                  <tr
                    key={e.id}
                    className={`border-b border-line last:border-0 hover:bg-canvas/60 ${
                      e.status !== 'ACTIVE' ? 'opacity-60' : ''
                    }`}
                  >
                    <td className="px-5 py-3">
                      <Link
                        to={`/students/${e.student.id}`}
                        className="font-medium text-ink hover:text-brand-700 hover:underline"
                      >
                        {e.student.fullName}
                      </Link>
                      {e.student.academyEmail && (
                        <span className="block text-[12px] text-ink-mute">
                          {e.student.academyEmail}
                        </span>
                      )}
                    </td>
                    {canEdit && (
                      <td className="tabular px-4 py-3 text-ink-soft">{e.student.phone ?? '—'}</td>
                    )}
                    {canEdit && (
                      <td className="tabular px-4 py-3 text-right text-ink-soft">
                        {(() => {
                          // ბოლო ისტორიული ეტაპი — მიმდინარე % ჟურნალიდან ცალკე ჩანს
                          const past = e.stageResults
                            .filter((r) => r.isHistorical)
                            .sort((a, b) => b.stage - a.stage)[0];
                          if (!past) return '—';
                          return (
                            <>
                              {formatPercent(past.attendancePct, 0)}
                              {past.label && (
                                <span className="block text-[11px] text-ink-mute">
                                  {past.label}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <DoneCell assessment={e.assessment} />
                    </td>
                    <td className="px-4 py-3">
                      <CertBadge assessment={e.assessment} />
                    </td>
                    <td className="px-4 py-3">
                      {canEdit ? (
                        <select
                          value={e.status}
                          onChange={(ev) => void changeStatus(e.id, ev.target.value)}
                          className="rounded-md border border-line-strong bg-surface px-2 py-1 text-[12px] focus:border-brand-600 focus:outline-none"
                        >
                          {ENROLLMENT_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {ENROLLMENT_STATUS_LABEL[s]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <Badge tone={e.status === 'ACTIVE' ? 'ok' : 'neutral'}>
                          {ENROLLMENT_STATUS_LABEL[e.status]}
                        </Badge>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {canEdit && (
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => setToTransfer(e)}
                            aria-label="სხვა ჯგუფში გადაყვანა"
                            title="სხვა ჯგუფში გადაყვანა"
                            className="rounded-md p-1.5 text-ink-mute transition hover:bg-canvas hover:text-brand-600"
                          >
                            <ArrowRightLeft className="size-4" />
                          </button>
                          <button
                            onClick={() => setToRemove(e)}
                            aria-label="ამოღება"
                            title="ჯგუფიდან ამოღება"
                            className="rounded-md p-1.5 text-ink-mute transition hover:bg-danger-soft hover:text-danger"
                          >
                            <UserMinus className="size-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <EnrollModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        groupId={groupId}
        enrolledIds={(enrollments ?? []).map((e) => e.student.id)}
      />

      <TransferModal
        enrollment={toTransfer}
        onClose={() => setToTransfer(null)}
        currentGroupId={groupId}
      />

      <ConfirmDialog
        open={Boolean(toRemove)}
        onClose={() => setToRemove(null)}
        onConfirm={() => void handleRemove()}
        title="ჯგუფიდან ამოღება"
        message={
          <>
            <strong>{toRemove?.student.fullName}</strong> ამოვარდება ამ ჯგუფიდან. თუ დასწრების
            ისტორია აქვს, ამოღება დაიბლოკება — მაშინ სტატუსი შეცვალე „შეწყვეტილით".
          </>
        }
        confirmLabel="ამოღება"
        danger
        busy={removeEnrollment.isPending}
      />
    </>
  );
}

// ============================================================
// ჩარიცხვა — ძებნა და მრავლობითი არჩევა
// ============================================================

function EnrollModal({
  open,
  onClose,
  groupId,
  enrolledIds,
}: {
  open: boolean;
  onClose: () => void;
  groupId: string;
  enrolledIds: string[];
}) {
  const toast = useToast();
  const enroll = useEnrollStudents(groupId);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const debouncedQ = useDebounced(q, 300);
  const { data, isLoading } = useStudents({ q: debouncedQ || undefined, pageSize: 50 }, open);

  const candidates = (data?.items ?? []).filter((s) => !enrolledIds.includes(s.id));

  function toggle(id: string) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function submit() {
    if (selected.length === 0) {
      toast.error('აირჩიე მინიმუმ ერთი სტუდენტი');
      return;
    }
    try {
      const res = await enroll.mutateAsync(selected);
      toast.success(
        res.skipped > 0
          ? `დაემატა ${res.added}, უკვე იყო ${res.skipped}`
          : `დაემატა ${res.added} სტუდენტი`,
      );
      setSelected([]);
      setQ('');
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ჩარიცხვა ვერ მოხერხდა');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="სტუდენტების ჩარიცხვა"
      description={selected.length > 0 ? `არჩეულია ${selected.length}` : 'მოძებნე და მონიშნე'}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={enroll.isPending}>
            გაუქმება
          </Button>
          <Button size="sm" onClick={() => void submit()} loading={enroll.isPending}>
            ჩარიცხვა ({selected.length})
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-mute" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            placeholder="სახელი, ელფოსტა ან ტელეფონი…"
            className="w-full rounded-lg border border-line-strong bg-surface py-2.5 pl-9 pr-3 text-sm transition placeholder:text-ink-mute focus:border-brand-600 focus:outline-none focus:ring-3 focus:ring-brand-100"
          />
        </div>

        <div className="max-h-72 overflow-y-auto rounded-lg border border-line">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : candidates.length === 0 ? (
            <p className="px-4 py-8 text-center text-[13px] text-ink-mute">
              შესაბამისი სტუდენტი ვერ მოიძებნა
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {candidates.map((s) => {
                const activeGroups = s.enrollments.filter((e) => e.status === 'ACTIVE');
                return (
                  <li key={s.id}>
                    <label className="flex cursor-pointer items-center gap-3 px-3.5 py-2.5 transition hover:bg-canvas">
                      <input
                        type="checkbox"
                        checked={selected.includes(s.id)}
                        onChange={() => toggle(s.id)}
                        className="size-4 shrink-0 accent-brand-600"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium text-ink">
                          {s.fullName}
                        </span>
                        <span className="tabular block truncate text-[12px] text-ink-mute">
                          {s.phone ?? s.email ?? '—'}
                        </span>
                      </span>
                      {activeGroups.length > 0 && (
                        <Badge className="tabular shrink-0">{activeGroups[0]!.group.name}</Badge>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p className="text-[12px] text-ink-mute">
          სტუდენტი შეიძლება ერთდროულად რამდენიმე ჯგუფში იყოს.
        </p>
      </div>
    </Modal>
  );
}

// ============================================================
// გადაყვანა სხვა ჯგუფში
// ============================================================

function TransferModal({
  enrollment,
  onClose,
  currentGroupId,
}: {
  enrollment: GroupEnrollment | null;
  onClose: () => void;
  currentGroupId: string;
}) {
  const toast = useToast();
  const transfer = useTransferEnrollment();
  const { data } = useGroups({ pageSize: 100 });
  const [targetGroupId, setTargetGroupId] = useState('');

  const options = (data?.items ?? [])
    .filter((g) => g.id !== currentGroupId && g.status !== 'CANCELLED')
    .map((g) => ({ value: g.id, label: `${g.name} — ${g.course.name}` }));

  async function submit() {
    if (!enrollment || !targetGroupId) {
      toast.error('აირჩიე ჯგუფი');
      return;
    }
    try {
      await transfer.mutateAsync({ id: enrollment.id, targetGroupId });
      toast.success('სტუდენტი გადაყვანილია');
      setTargetGroupId('');
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'გადაყვანა ვერ მოხერხდა');
    }
  }

  return (
    <Modal
      open={Boolean(enrollment)}
      onClose={onClose}
      size="sm"
      title="სხვა ჯგუფში გადაყვანა"
      description={enrollment?.student.fullName}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={transfer.isPending}>
            გაუქმება
          </Button>
          <Button size="sm" onClick={() => void submit()} loading={transfer.isPending}>
            გადაყვანა
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Select
          label="ახალი ჯგუფი"
          value={targetGroupId}
          onChange={(e) => setTargetGroupId(e.target.value)}
          placeholder="აირჩიე ჯგუფი"
          options={options}
        />
        <p className="rounded-lg bg-canvas px-3 py-2.5 text-[12px] leading-relaxed text-ink-soft">
          ძველი ჩარიცხვა რჩება სტატუსით „გადაყვანილი" — დასწრების ისტორია არ იკარგება.
        </p>
      </div>
    </Modal>
  );
}
